import { createElement } from 'react';
import { isPointButtonRatingUnit } from '@digital-twins/shared-types';
import {
  ReactQuestionFactory,
  RendererFactory,
  SurveyQuestionElementBase,
  type ItemValue,
  type Model,
  type Question,
  type QuestionMatrixBaseModel,
  type QuestionSelectBase,
} from 'survey-react';

/**
 * How the preview lays out a scale (C04-2, option A): the preview is the one
 * SurveyJS renderer in the product, and these are runtime properties of its
 * model only. The compiled JSON twins read is never changed: `rating`,
 * `radiogroup`, `matrix` and `matrixdropdown` keep their shapes and choices.
 *
 * - A point scale (`isPointButtonRatingUnit`, from the rating-scale registry)
 *   is answered on buttons. A `rating` question or cell gets
 *   `displayMode: "buttons"`, so SurveyJS wraps them and never switches to its
 *   "Select…" dropdown. A scale with N/A compiles to a `radiogroup`, or to a
 *   `dropdown` cell in a grid; each renders as `SCALE_BUTTONS`, the same
 *   buttons with N/A last.
 * - A grid shows as a table only while the table fits (`gridLayout`);
 *   otherwise each row is a list, so no cell is ever off screen.
 *
 * A yes/no question and a legacy `rating_0_100` keep SurveyJS's default.
 */

export const SCALE_BUTTONS = 'scale-buttons';
const SCALE_BUTTONS_COMPONENT = 'sv-preview-scale-buttons';

type ScaleElement = { type?: unknown; name?: unknown; _canonical?: { unit?: unknown; measures?: unknown } };
type CssMap = Record<string, string | undefined>;

/** The theme's rating classes: SurveyJS's theme owns how a scale button looks. */
export function ratingClasses(question: Question): CssMap {
  const survey = question.survey as unknown as { css?: { rating?: CssMap } } | undefined;
  return survey?.css?.rating ?? {};
}

function classNames(...names: Array<string | false | undefined>): string {
  return names.filter(Boolean).join(' ');
}

/**
 * A choice-based scale (a `radiogroup` with N/A, or a `dropdown` cell with
 * N/A) as rating buttons, N/A last: SurveyJS's rating-item markup with the
 * theme's `css.rating` classes, one native radio per choice.
 */
export class SurveyQuestionScaleButtons extends SurveyQuestionElementBase {
  protected get question(): QuestionSelectBase {
    return this.questionBase as QuestionSelectBase;
  }

  protected renderElement(): JSX.Element {
    const question = this.question;
    const rating = ratingClasses(question);
    const readOnly = question.isReadOnly;
    const onError = question.errors.length > 0;
    return (
      <div className={classNames(rating.root, rating.rootWrappable)} ref={element => this.setControl(element)}>
        <fieldset
          role="radiogroup"
          aria-required={question.a11y_input_ariaRequired}
          aria-label={question.a11y_input_ariaLabel}
          aria-labelledby={question.a11y_input_ariaLabelledBy}
          aria-describedby={question.a11y_input_ariaDescribedBy}
          aria-invalid={question.a11y_input_ariaInvalid}
          aria-errormessage={question.a11y_input_ariaErrormessage}
        >
          <legend role="presentation" className="sv-hidden" />
          {question.visibleChoices.map((item: ItemValue, index: number) => {
            const selected = question.value == item.value;
            const fixedSize = item.text.length <= 2 && Number.isInteger(Number(item.text));
            return (
              <label
                key={`${String(item.value)}-${index}`}
                className={classNames(
                  rating.item,
                  selected && rating.selected,
                  readOnly && rating.itemReadOnly,
                  !readOnly && !selected && rating.itemHover,
                  onError && rating.itemOnError,
                  fixedSize && rating.itemFixedSize,
                )}
              >
                <input
                  type="radio"
                  className="sv-visuallyhidden"
                  name={question.id}
                  id={`${question.inputId}_${index}`}
                  value={String(item.value)}
                  checked={selected}
                  disabled={readOnly || !item.isEnabled}
                  onChange={() => { question.renderedValue = item.value; }}
                />
                <span className={rating.itemText} data-text={item.text}>{this.renderLocString(item.locText)}</span>
              </label>
            );
          })}
        </fieldset>
      </div>
    );
  }
}

let registered = false;
/** Registers `SCALE_BUTTONS` for both question types it replaces, once. */
export function registerScaleButtons(): void {
  if (registered) return;
  registered = true;
  ReactQuestionFactory.Instance.registerQuestion(SCALE_BUTTONS_COMPONENT, props => createElement(SurveyQuestionScaleButtons, props));
  RendererFactory.Instance.registerRenderer('radiogroup', SCALE_BUTTONS, SCALE_BUTTONS_COMPONENT);
  RendererFactory.Instance.registerRenderer('dropdown', SCALE_BUTTONS, SCALE_BUTTONS_COMPONENT);
}

/**
 * A grid's layout from its widths, both in the question's own box:
 * `requiredWidth` is what the question needs to show its table (measured in
 * table mode), `availableWidth` what the question has now. "list" when the
 * table would not fit; otherwise "auto", never a forced "table", so SurveyJS's
 * own narrow-screen rule still applies.
 */
export function gridLayout(requiredWidth: number, availableWidth: number): 'list' | 'auto' {
  return requiredWidth > availableWidth ? 'list' : 'auto';
}

/** What a grid's table width depends on: its visible columns, their choices and the locale. */
function gridSignature(survey: Model, matrix: QuestionMatrixBaseModel<unknown, unknown>): string {
  const columns = (matrix.visibleColumns as Array<{ name?: string; value?: unknown; text?: string; choices?: Array<ItemValue> }>)
    .map(column => [column.name ?? column.value, column.text ?? '', (column.choices ?? []).map(choice => choice.text)]);
  return JSON.stringify([survey.locale, columns]);
}

/** The point-scale units each compiled element carries, by name; a grid's by column. */
function scaleUnits(surveyJson: Record<string, unknown>) {
  const elements = new Set<string>();
  const columns = new Map<string, Set<string>>();
  const pages = surveyJson.pages as Array<{ elements?: Array<ScaleElement> }> | undefined;
  pages?.forEach(page => page.elements?.forEach(element => {
    if (typeof element.name !== 'string') return;
    if (isPointButtonRatingUnit(element._canonical?.unit)) elements.add(element.name);
    const measures = element._canonical?.measures;
    if (element.type === 'matrixdropdown' && Array.isArray(measures)) {
      columns.set(element.name, new Set(measures
        .filter(measure => isPointButtonRatingUnit((measure as { unit?: unknown } | null)?.unit))
        .map(measure => String((measure as { id?: unknown }).id))));
    }
  }));
  return { elements, columns };
}

function presentScale(question: Question): void {
  if (question.getType() === 'rating') {
    (question as unknown as { displayMode: string }).displayMode = 'buttons';
    // SurveyJS names every rating radio by the question's title
    // (`aria-label = ariaLabel`), so each point read as the title. Without it
    // each radio takes its point's label; the group keeps the title.
    Object.defineProperty(question, 'ariaLabel', { get: () => null, configurable: true });
  } else if (question.getType() === 'radiogroup' || question.getType() === 'dropdown') question.renderAs = SCALE_BUTTONS;
}

/**
 * Applies the preview's scale layout to `survey`, built from `surveyJson`
 * (`previewSurveyModel`). Reads the compiled JSON; never writes it.
 */
export function applyScalePresentation(survey: Model, surveyJson: Record<string, unknown>): void {
  registerScaleButtons();
  const { elements, columns } = scaleUnits(surveyJson);
  elements.forEach(name => {
    const question = survey.getQuestionByName(name);
    if (question) presentScale(question);
  });
  survey.onMatrixCellCreated.add((_, options) => {
    if (columns.get(options.question.name)?.has(options.columnName)) presentScale(options.cellQuestion);
  });

  // Each grid's required width, measured in table mode, where an overflowing
  // table makes the question scroll sideways: `scrollWidth` is then exactly
  // what the question needs. It is kept for list mode, where the table cannot
  // be measured, until the grid's columns, their choices or the locale
  // change. The decision reads only the question's current width, which does
  // not depend on the layout, so a resize crosses each threshold once.
  const required = new WeakMap<Question, { signature: string; width: number }>();
  const rendered = new Map<Question, HTMLElement>();
  const layout = (question: Question, root: HTMLElement) => {
    const matrix = question as unknown as QuestionMatrixBaseModel<unknown, unknown>;
    const signature = gridSignature(survey, matrix);
    const mobileClass = (question.cssClasses as CssMap).mobile;
    const showsTable = !matrix.isMobile && !(mobileClass && root.classList.contains(mobileClass));
    if (showsTable) {
      if (root.scrollWidth <= root.clientWidth) return;
      required.set(question, { signature, width: root.scrollWidth });
    }
    const cached = required.get(question);
    if (!cached) return;
    if (cached.signature !== signature) {
      // Stale: show the table once to measure it again.
      required.delete(question);
      if (matrix.displayMode === 'list') {
        matrix.displayMode = 'auto';
        requestAnimationFrame(() => layout(question, root));
      }
      return;
    }
    const next = gridLayout(cached.width, root.clientWidth);
    if (matrix.displayMode !== next) matrix.displayMode = next;
  };
  survey.onAfterRenderQuestion.add((_, { question, htmlElement }) => {
    if (question.getType() !== 'matrix' && question.getType() !== 'matrixdropdown') return;
    rendered.set(question, htmlElement);
    layout(question, htmlElement);
    // Its cells render after the question itself.
    requestAnimationFrame(() => layout(question, htmlElement));
  });
  // After SurveyJS's own resize handling has rendered.
  survey.onResize.add(() => requestAnimationFrame(() => {
    rendered.forEach((root, question) => {
      if (root.isConnected) layout(question, root);
      else rendered.delete(question);
    });
  }));
}
